/**
 * RecoveryExerciseTestingPage - Link and test recovery packages with exercises
 * ISO 22301 Clause 8.5 - Exercising and Testing
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Play,
  Clock,
  AlertTriangle,
  Target,
  Zap,
  Timer,
  ClipboardCheck,
  FileText,
  Plus,
  Activity,
  TrendingUp,
  Calendar,
  Users,
  Award,
  BarChart3,
} from "lucide-react";

const RESULT_STATUS = {
  passed: { label: "Passed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  failed: { label: "Failed", color: "bg-red-100 text-red-700", icon: XCircle },
  partial: { label: "Partial", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700", icon: Clock },
  skipped: { label: "Skipped", color: "bg-slate-100 text-slate-500", icon: Clock },
};

export default function RecoveryExerciseTestingPage() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [exerciseTests, setExerciseTests] = useState([]);
  const [availableExercises, setAvailableExercises] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("packages");

  // Dialog states
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [procedureResults, setProcedureResults] = useState([]);
  const [testNotes, setTestNotes] = useState("");
  const [rtoAchieved, setRtoAchieved] = useState("");
  const [overallResult, setOverallResult] = useState("pending");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch approved recovery packages
      const pkgResponse = await api.get("/recovery-packages/all");
      const approvedPackages = pkgResponse.data.filter(p => p.status === "approved");
      setPackages(approvedPackages);

      // Fetch exercise stats
      const statsResponse = await api.get("/recovery-packages/exercise-stats");
      setStats(statsResponse.data);

      // Fetch available exercises
      const exercisesResponse = await api.get("/recovery-packages/available-exercises");
      setAvailableExercises(exercisesResponse.data);

      // If packageId provided, select it
      if (packageId) {
        const pkg = approvedPackages.find(p => p.id === packageId);
        if (pkg) {
          setSelectedPackage(pkg);
          const testsResponse = await api.get(`/recovery-packages/${packageId}/exercises`);
          setExerciseTests(testsResponse.data.exercise_tests || []);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [api, packageId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectPackage = async (pkg) => {
    setSelectedPackage(pkg);
    try {
      const testsResponse = await api.get(`/recovery-packages/${pkg.id}/exercises`);
      setExerciseTests(testsResponse.data.exercise_tests || []);
    } catch (error) {
      console.error("Failed to fetch exercise tests:", error);
      setExerciseTests([]);
    }
  };

  const handleLinkExercise = async () => {
    if (!selectedExercise || !selectedPackage) return;

    setSubmitting(true);
    try {
      await api.post(`/recovery-packages/${selectedPackage.id}/exercises`, {
        exercise_id: selectedExercise.id,
        exercise_name: selectedExercise.name,
        exercise_type: selectedExercise.exercise_type,
        procedure_results: [],
        overall_result: "pending",
      });

      toast.success("Exercise linked to recovery package");
      setShowLinkDialog(false);
      setSelectedExercise(null);
      fetchData();
    } catch (error) {
      console.error("Failed to link exercise:", error);
      toast.error("Failed to link exercise");
    } finally {
      setSubmitting(false);
    }
  };

  const openTestDialog = (test) => {
    setSelectedTest(test);
    // Initialize procedure results from recovery steps
    const steps = selectedPackage?.application_recovery?.steps || [];
    const existingResults = test.procedure_results || [];

    const results = steps.map((step, idx) => {
      const existing = existingResults.find(r => r.step_index === idx);
      return existing || {
        step_index: idx,
        step_action: step.action,
        status: "pending",
        actual_duration_mins: step.duration_mins,
        notes: "",
        issues_found: "",
      };
    });

    setProcedureResults(results);
    setTestNotes(test.notes || "");
    setRtoAchieved(test.rto_achieved_hours?.toString() || "");
    setOverallResult(test.overall_result || "pending");
    setShowTestDialog(true);
  };

  const handleSaveTestResults = async (complete = false) => {
    if (!selectedTest || !selectedPackage) return;

    setSubmitting(true);
    try {
      await api.put(`/recovery-packages/${selectedPackage.id}/exercises/${selectedTest.test_id}`, {
        procedure_results: procedureResults,
        overall_result: overallResult,
        rto_achieved_hours: rtoAchieved ? parseFloat(rtoAchieved) : null,
        notes: testNotes,
        completed: complete,
      });

      toast.success(complete ? "Exercise test completed!" : "Test results saved");
      setShowTestDialog(false);
      setSelectedTest(null);
      fetchData();
    } catch (error) {
      console.error("Failed to save test results:", error);
      toast.error("Failed to save results");
    } finally {
      setSubmitting(false);
    }
  };

  const updateProcedureResult = (index, field, value) => {
    setProcedureResults(prev => prev.map((r, i) => 
      i === index ? { ...r, [field]: value } : r
    ));
  };

  // Calculate overall result based on procedure results
  useEffect(() => {
    if (procedureResults.length === 0) return;

    const passed = procedureResults.filter(r => r.status === "passed").length;
    const failed = procedureResults.filter(r => r.status === "failed").length;
    const total = procedureResults.filter(r => r.status !== "skipped").length;

    if (failed > 0 && failed === total) {
      setOverallResult("failed");
    } else if (passed === total && total > 0) {
      setOverallResult("passed");
    } else if (passed > 0 || failed > 0) {
      setOverallResult("partial");
    }
  }, [procedureResults]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recovery-exercise-testing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 8.5</Badge>
            <Badge className="bg-sky-100 text-sky-700">BCM Manager</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Recovery Package Exercise Testing</h1>
          <p className="text-slate-600 mt-1">
            Validate recovery procedures through structured exercises
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Approved Packages</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total_approved_packages}</p>
            </CardContent>
          </Card>
          <Card className={stats.untested_packages > 0 ? "border-amber-200 bg-amber-50" : ""}>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Untested</p>
              <p className="text-2xl font-bold text-amber-600">{stats.untested_packages}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Testing Coverage</p>
              <p className="text-2xl font-bold text-sky-700">{stats.testing_coverage_pct}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Passed Tests</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.passed_packages}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">RTO Success Rate</p>
              <p className="text-2xl font-bold text-blue-600">{stats.rto_success_rate}%</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Package List */}
        <Card className="col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Approved Packages</CardTitle>
            <CardDescription>Select a package to manage exercise tests</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {packages.length === 0 ? (
                <div className="p-4 text-center text-slate-500">No approved packages</div>
              ) : (
                packages.map((pkg) => {
                  const hasTests = pkg.exercise_tests?.length > 0;
                  const lastTest = hasTests ? 
                    pkg.exercise_tests.sort((a, b) => b.created_at?.localeCompare(a.created_at))[0] : null;
                  
                  return (
                    <div
                      key={pkg.id}
                      className={`p-3 cursor-pointer hover:bg-slate-50 ${
                        selectedPackage?.id === pkg.id ? "bg-sky-50 border-l-4 border-sky-700" : ""
                      }`}
                      onClick={() => selectPackage(pkg)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{pkg.process_name}</p>
                          <p className="text-xs text-slate-500">{pkg.department}</p>
                        </div>
                        {hasTests ? (
                          <Badge className={RESULT_STATUS[lastTest?.overall_result]?.color || ""}>
                            {RESULT_STATUS[lastTest?.overall_result]?.label}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">No Tests</Badge>
                        )}
                      </div>
                      {pkg.last_tested && (
                        <p className="text-xs text-slate-400 mt-1">
                          Last tested: {new Date(pkg.last_tested).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Exercise Tests Panel */}
        <Card className="col-span-2">
          {!selectedPackage ? (
            <CardContent className="pt-12 text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Select a package to view exercise tests</p>
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{selectedPackage.process_name}</CardTitle>
                    <CardDescription>
                      RTO: {selectedPackage.bia_summary?.rto_hours}h | 
                      Strategy: {selectedPackage.recovery_strategy?.strategy_type?.replace("_", " ")}
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowLinkDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Link Exercise
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {exerciseTests.length === 0 ? (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-4">No exercise tests linked to this package</p>
                    <Button variant="outline" onClick={() => setShowLinkDialog(true)}>
                      Link First Exercise
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {exerciseTests.map((test) => {
                      const resultConfig = RESULT_STATUS[test.overall_result] || RESULT_STATUS.pending;
                      const ResultIcon = resultConfig.icon;
                      const passedSteps = test.procedure_results?.filter(r => r.status === "passed").length || 0;
                      const totalSteps = test.procedure_results?.length || 0;
                      
                      return (
                        <Card key={test.test_id} className="border">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${resultConfig.color}`}>
                                  <ResultIcon className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="font-medium">{test.exercise_name}</p>
                                  <p className="text-sm text-slate-500 capitalize">
                                    {test.exercise_type?.replace("_", " ")} Exercise
                                  </p>
                                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                                    <span>Date: {new Date(test.test_date).toLocaleDateString()}</span>
                                    {totalSteps > 0 && (
                                      <span>Steps: {passedSteps}/{totalSteps} passed</span>
                                    )}
                                    {test.rto_achieved_hours && (
                                      <span className={
                                        test.rto_achieved_hours <= selectedPackage.bia_summary?.rto_hours
                                          ? "text-emerald-600"
                                          : "text-red-600"
                                      }>
                                        RTO Achieved: {test.rto_achieved_hours}h
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openTestDialog(test)}
                              >
                                <Play className="w-4 h-4 mr-1" />
                                {test.completed_at ? "View Results" : "Run Test"}
                              </Button>
                            </div>
                            {test.notes && (
                              <p className="text-sm text-slate-600 mt-3 p-2 bg-slate-50 rounded">
                                {test.notes}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Link Exercise Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Exercise to Recovery Package</DialogTitle>
            <DialogDescription>
              Select an exercise to test the recovery procedures for "{selectedPackage?.process_name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {availableExercises.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-slate-500">No available exercises</p>
                <Button variant="outline" className="mt-2" onClick={() => navigate("/exercises")}>
                  Create Exercise
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {availableExercises.map((ex) => (
                  <div
                    key={ex.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedExercise?.id === ex.id
                        ? "border-sky-700 bg-sky-50"
                        : "hover:border-slate-300"
                    }`}
                    onClick={() => setSelectedExercise(ex)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{ex.name}</p>
                        <p className="text-sm text-slate-500 capitalize">
                          {ex.exercise_type?.replace("_", " ")} • {ex.status}
                        </p>
                      </div>
                      {ex.scheduled_date && (
                        <Badge variant="outline">
                          {new Date(ex.scheduled_date).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleLinkExercise} 
              disabled={!selectedExercise || submitting}
            >
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Link Exercise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Execution Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Exercise Test: {selectedTest?.exercise_name}</DialogTitle>
            <DialogDescription>
              Test recovery procedures for "{selectedPackage?.process_name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {/* Procedure Steps */}
            <div>
              <Label className="text-base font-semibold">Recovery Procedure Steps</Label>
              <p className="text-sm text-slate-500 mb-3">
                Mark each step as Passed, Failed, Partial, or Skipped
              </p>
              
              {procedureResults.length === 0 ? (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4">
                    <p className="text-amber-800">
                      No recovery steps defined for this package. Add steps in the Recovery Wizard first.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {procedureResults.map((result, idx) => (
                    <Card key={idx} className="border">
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          <Badge variant="outline" className="w-8 justify-center mt-1">
                            {idx + 1}
                          </Badge>
                          <div className="flex-1">
                            <p className="font-medium">{result.step_action}</p>
                            <div className="grid grid-cols-4 gap-2 mt-2">
                              <Select
                                value={result.status}
                                onValueChange={(v) => updateProcedureResult(idx, "status", v)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="passed">✓ Passed</SelectItem>
                                  <SelectItem value="failed">✗ Failed</SelectItem>
                                  <SelectItem value="partial">~ Partial</SelectItem>
                                  <SelectItem value="skipped">⊘ Skipped</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input
                                type="number"
                                value={result.actual_duration_mins}
                                onChange={(e) => updateProcedureResult(idx, "actual_duration_mins", parseInt(e.target.value) || 0)}
                                placeholder="Mins"
                                className="h-8"
                              />
                              <Input
                                value={result.issues_found || ""}
                                onChange={(e) => updateProcedureResult(idx, "issues_found", e.target.value)}
                                placeholder="Issues found"
                                className="h-8 col-span-2"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* RTO Achievement */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target RTO (from BIA)</Label>
                <Input
                  value={`${selectedPackage?.bia_summary?.rto_hours || "?"} hours`}
                  disabled
                  className="bg-slate-50"
                />
              </div>
              <div>
                <Label>Actual RTO Achieved (hours)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={rtoAchieved}
                  onChange={(e) => setRtoAchieved(e.target.value)}
                  placeholder="Enter actual recovery time"
                />
                {rtoAchieved && selectedPackage?.bia_summary?.rto_hours && (
                  <p className={`text-xs mt-1 ${
                    parseFloat(rtoAchieved) <= selectedPackage.bia_summary.rto_hours
                      ? "text-emerald-600"
                      : "text-red-600"
                  }`}>
                    {parseFloat(rtoAchieved) <= selectedPackage.bia_summary.rto_hours
                      ? "✓ RTO target met"
                      : "✗ RTO target exceeded"}
                  </p>
                )}
              </div>
            </div>

            {/* Overall Result */}
            <div>
              <Label>Overall Test Result</Label>
              <Select value={overallResult} onValueChange={setOverallResult}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passed">✓ Passed - All objectives met</SelectItem>
                  <SelectItem value="partial">~ Partial - Some objectives met</SelectItem>
                  <SelectItem value="failed">✗ Failed - Critical issues found</SelectItem>
                  <SelectItem value="pending">⊘ Pending - Not completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label>Test Notes & Observations</Label>
              <Textarea
                value={testNotes}
                onChange={(e) => setTestNotes(e.target.value)}
                placeholder="Document findings, issues, recommendations..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSaveTestResults(false)}
              disabled={submitting}
            >
              Save Draft
            </Button>
            <Button
              onClick={() => handleSaveTestResults(true)}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Complete Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
